import { useState, useEffect } from "react";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Store, Loader2, AlertCircle, Check } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";

const SIGNUP_FEE_DISPLAY = "$100.00";

/** Mirrors the server-side SLUG_RE so the user sees the rule before submitting. */
const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,78}[a-z0-9]$/;

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);
}

export default function SellerJoin() {
  const [displayName, setDisplayName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [bio, setBio] = useState("");
  const [referralSlug, setReferralSlug] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Referral code arrives as ?ref=<storefront-slug>. Captured on mount so it
  // survives the user editing the form, and sent to the server which resolves
  // it to a profile — the client never names the upline directly.
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) setReferralSlug(ref.toLowerCase());
  }, []);

  // Auto-derive the storefront address until the user edits it themselves.
  useEffect(() => {
    if (!slugTouched) setSlug(slugify(displayName));
  }, [displayName, slugTouched]);

  const slugValid = SLUG_RE.test(slug);
  const canSubmit = displayName.trim().length >= 2 && slugValid && !submitting;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch("/api/marketplace/seller/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          displayName: displayName.trim(),
          slug,
          bio: bio.trim() || undefined,
          referralSlug: referralSlug ?? undefined,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data.error || "Could not start checkout. Please try again.");
        setSubmitting(false);
        return;
      }

      if (!data.approveUrl) {
        setError("Checkout is unavailable right now. Please try again shortly.");
        setSubmitting(false);
        return;
      }

      // Hand off to PayPal. Deliberately not resetting `submitting` — the page
      // is navigating away, and re-enabling the button invites a double order.
      window.location.href = data.approveUrl;
    } catch {
      setError("Network error. Please check your connection and try again.");
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-[9999] bg-background/95 backdrop-blur border-b">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <SidebarTrigger className="h-8 w-8" data-testid="button-sidebar-toggle" />
          <Store className="h-5 w-5 text-amber-500" />
          <h1 className="text-lg font-bold">Open a Storefront</h1>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        {referralSlug && (
          <Alert data-testid="alert-referred-by">
            <Check className="h-4 w-4" />
            <AlertDescription>
              You were referred by <strong>{referralSlug}</strong>.
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Your storefront</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="displayName">Store name</Label>
                <Input
                  id="displayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Grace Handmade Goods"
                  maxLength={120}
                  required
                  data-testid="input-display-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="slug">Storefront address</Label>
                <div className="flex items-center gap-1 text-sm">
                  <span className="text-muted-foreground shrink-0">/store/</span>
                  <Input
                    id="slug"
                    value={slug}
                    onChange={(e) => { setSlugTouched(true); setSlug(slugify(e.target.value)); }}
                    placeholder="grace-handmade"
                    maxLength={80}
                    required
                    data-testid="input-slug"
                  />
                </div>
                {slug && !slugValid && (
                  <p className="text-xs text-destructive" data-testid="text-slug-error">
                    Use 3–80 lowercase letters, numbers or dashes.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio">About your store <span className="text-muted-foreground">(optional)</span></Label>
                <Textarea
                  id="bio"
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="What you make and who it's for."
                  maxLength={2000}
                  rows={4}
                  data-testid="input-bio"
                />
              </div>

              {error && (
                <Alert variant="destructive" data-testid="alert-signup-error">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="rounded-lg border p-4 space-y-1">
                <div className="flex items-center justify-between font-medium">
                  <span>One-time setup fee</span>
                  <span data-testid="text-signup-fee">{SIGNUP_FEE_DISPLAY}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  You keep 100% of what your products sell for. Payments for your
                  products go directly to your own PayPal account.
                </p>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={!canSubmit}
                data-testid="button-continue-to-paypal"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Redirecting to PayPal…</>
                ) : (
                  <>Continue to PayPal · {SIGNUP_FEE_DISPLAY}</>
                )}
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                Your storefront activates once payment completes.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
